import { CodeTtl } from '@euler/app/components/CodeTtl';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { maskedMobilePhone } from '@euler/app/tv/functions/maskedMobilePhone';
import { useModalWrapperSize } from '@euler/components/ModalWrapper';
import { FormGroup } from '@euler/components/form';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBackButtonHandler } from '@euler/functions/useBackButtonHandler';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInputEndEditingEventData,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import { BackButton } from '../components/BackButton';
import { CustomPageHeader } from '../components/CustomPageHeader';

const kCellMargin = 5;

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        codeFieldRoot: {
          paddingHorizontal: 0,
          justifyContent: 'space-between',
        },
        codeFieldInput: {
          borderRadius: rpx(8),
        },
      }),
    [rpx],
  );
};

type CodeInfo = {
  ticket: string;
  length: number;
  ttl: number;
};

export const VerifyCodeScreen = wrapNavigatorScreen(
  ({
    mobile,
    compact = true,
    onSendCode,
    onDone,
    ...props
  }: {
    mobile: string;
    codeInfo: CodeInfo;
    onSendCode: (
      callback: (error: Error | null | undefined, result?: CodeInfo) => void,
    ) => void;
    compact?: boolean;
    onDone: (params: {
      mobile: string;
      ticket: string;
      verifyCode: string;
    }) => any;
  }) => {
    const theme = useTheme();
    const contentContainerStyle = useScreenContentContainerStyle();
    const { rpx } = useResponsiveContext();
    const onGoBack = useBackButtonHandler();
    const insets = useSafeAreaInsets();
    const cellMargin = rpx(kCellMargin);
    const styles = useStyles();

    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();

    const [codeInfo, setCodeInfo] = useState(props.codeInfo);
    const { length, ttl, ticket } = codeInfo;
    const [value, setValue] = useState('');
    const ref = useBlurOnFulfill({ value, cellCount: length });
    const [codeFieldProps, getCellOnLayoutHandler] = useClearByFocusCell({
      value,
      setValue,
    });
    const [isTicking, setIsTicking] = useState(true);
    const [cellSize, setCellSize] = useState(rpx(10));

    useModalWrapperSize({
      width: compact ? rpx(375) : undefined,
      height: compact ? rpx(500) : undefined,
    });

    const onTimeout = useCallback(() => {
      setIsTicking(false);
    }, []);

    const onProblemReceiveCode = useCallback(() => {
      navigation.push('_codeTips', {});
    }, [navigation]);

    const onCodeFieldLayout = useCallback(
      (e: LayoutChangeEvent) => {
        const size =
          (e.nativeEvent.layout.width - (length - 1) * 2 * cellMargin) / length;
        setCellSize(size);
      },
      [cellMargin, length],
    );

    const onValueChange = useCallback(
      (text: string) => {
        setValue(text);
        if (Platform.OS === 'web' && text.length === length) {
          onDone({
            mobile,
            ticket,
            verifyCode: text,
          });
        }
      },
      [length, mobile, onDone, ticket],
    );

    const onResendCode = useCallback(() => {
      setValue('');
      onSendCode((error, result) => {
        if (error) return;
        setCodeInfo(result!);
        setIsTicking(true);
        ref.current?.focus();
      });
    }, [onSendCode, ref]);

    const onEditEditing = useCallback(
      async (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
        const code = e.nativeEvent.text;
        if (code.length < length) return;
        onDone({
          mobile,
          ticket,
          verifyCode: code,
        });
      },
      [length, onDone, mobile, ticket],
    );

    return (
      <>
        <ScrollView
          contentContainerStyle={[
            {
              paddingTop: insets.top + rpx(96),
              paddingLeft: rpx(20),
              paddingRight: rpx(20),
              paddingBottom: rpx(20),
            },
            contentContainerStyle,
          ]}
        >
          <CustomPageHeader
            title="输入验证码"
            subTitle={`验证码已发送至${maskedMobilePhone(mobile)}`}
          />

          <View
            css={`
              border-radius: ${rpx(20)}px;
              margin-bottom: ${rpx(32)}px;
              margin-top: ${rpx(32)}px;
            `}
          >
            <FormGroup
              css={`
                align-items: stretch;
              `}
            >
              <CodeField
                ref={ref}
                {...codeFieldProps}
                value={value}
                onChangeText={onValueChange}
                cellCount={length}
                rootStyle={styles.codeFieldRoot}
                textInputStyle={styles.codeFieldInput}
                keyboardType="number-pad"
                textContentType="oneTimeCode"
                autoFocus
                onLayout={onCodeFieldLayout}
                onEndEditing={onEditEditing}
                renderCell={({ index, symbol, isFocused }) => (
                  <Text
                    key={index}
                    onLayout={getCellOnLayoutHandler(index)}
                    css={`
                      margin-left: ${index === 0 ? 0 : cellMargin}px;
                      margin-right: ${index === length - 1 ? 0 : cellMargin}px;
                      height: ${cellSize}px;
                      width: ${cellSize}px;
                      line-height: ${cellSize - cellMargin}px;
                      font-family: ${FontFamily.NotoSans.Medium};
                      font-size: ${rpx(24)}px;
                      text-align: center;
                      color: #333;
                      border-radius: ${rpx(5)}px;
                      overflow: hidden;
                      background-color: #fff;
                      elevation: 3;
                    `}
                  >
                    {symbol || (isFocused ? <Cursor /> : null)}
                  </Text>
                )}
              />
              <View
                css={`
                  flex-direction: row;
                  justify-content: space-between;
                  align-items: center;
                  flex-wrap: nowrap;
                  margin-top: ${rpx(10)}px;
                `}
              >
                {isTicking ? (
                  <CodeTtl ttl={ttl} onTimeout={onTimeout} />
                ) : (
                  <TouchableOpacity onPress={onResendCode}>
                    <Text
                      css={`
                        font-family: ${FontFamily.NotoSans.Light};
                        font-size: ${rpx(14)}px;
                        color: ${theme.link};
                      `}
                    >
                      重新获取验证码
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={onProblemReceiveCode}>
                  <Text
                    css={`
                      font-family: ${FontFamily.NotoSans.Light};
                      font-size: 14px;
                      color: ${theme.link};
                    `}
                  >
                    收不到验证码?
                  </Text>
                </TouchableOpacity>
              </View>
            </FormGroup>
          </View>
        </ScrollView>
        <BackButton
          onPress={onGoBack}
          color={'#333'}
          css={`
            position: absolute;
            left: ${rpx(20)}px;
            top: ${insets.top + rpx(15)}px;
          `}
        />
      </>
    );
  },
  { headerShown: false },
);
